import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getPayslipDetails, clearError } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const PayslipDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { payslipDetails, isLoading, error } = useSelector((state) => state.auth);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('payslips.details.title');
  }, [setTitle]);

  useEffect(() => {
    if (id) {
      dispatch(getPayslipDetails({ id, language: i18n.language }));
    }
  }, [dispatch, id, i18n.language]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString('en-US') + ' ' + t('payslips.currency');
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div>{t('payslips.loading', { defaultValue: isRTL ? 'جاري التحميل...' : 'Loading...' })}</div>
      </div>
    );
  }

  if (!payslipDetails) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div>{t('payslips.details.noData', { defaultValue: isRTL ? 'لا توجد بيانات' : 'No data available' })}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* العنوان */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => navigate('/payslips')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft />
            {t('payslips.details.back', { defaultValue: isRTL ? 'العودة' : 'Back' })}
          </button>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            textAlign
          }}>
            {t('payslips.details.title', { defaultValue: isRTL ? 'تفاصيل كشف الراتب' : 'Payslip Details' })}
          </h1>
        </div>
      </div>

      {/* تفاصيل كشف الراتب */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {/* فترة الراتب */}
          <div>
            <h3 style={{ 
              color: 'white',
              marginBottom: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign,
              direction: dir
            }}>
              {t('payslips.details.payPeriod', { defaultValue: isRTL ? 'فترة الراتب' : 'Pay Period' })}
            </h3>
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #333',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: 'white'
              }}>
                {payslipDetails.pay_period || t('payslips.notAvailable', { defaultValue: isRTL ? 'غير متوفر' : 'Not Available' })}
              </div>
            </div>
          </div>

          {/* تاريخ التوليد */}
          <div>
            <h3 style={{ 
              color: 'white',
              marginBottom: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign,
              direction: dir
            }}>
              {t('payslips.details.generatedAt', { defaultValue: isRTL ? 'تاريخ التوليد' : 'Generated At' })}
            </h3>
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #333',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: 'white'
              }}>
                {payslipDetails.generated_at ? formatDate(payslipDetails.generated_at) : t('payslips.notAvailable', { defaultValue: isRTL ? 'غير متوفر' : 'Not Available' })}
              </div>
            </div>
          </div>

          {/* الموظف */}
          <div>
            <h3 style={{ 
              color: 'white',
              marginBottom: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign,
              direction: dir
            }}>
              {t('payslips.details.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })}
            </h3>
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #333',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: 'white'
              }}>
                {(isRTL ? payslipDetails.employee?.name_ar : payslipDetails.employee?.name_en) || payslipDetails.employee?.name || t('payslips.notAvailable', { defaultValue: isRTL ? 'غير متوفر' : 'Not Available' })}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#ccc',
                marginTop: '5px'
              }}>
                {payslipDetails.employee?.employee_code || '-'}
              </div>
            </div>
          </div>

          {/* إجمالي الراتب */}
          <div>
            <h3 style={{ 
              color: 'white',
              marginBottom: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign,
              direction: dir
            }}>
              {t('payslips.details.grossSalary', { defaultValue: isRTL ? 'إجمالي الراتب' : 'Gross Salary' })}
            </h3>
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #333',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                color: 'white'
              }}>
                {payslipDetails.gross_salary ? formatCurrency(payslipDetails.gross_salary) : t('payslips.notAvailable', { defaultValue: isRTL ? 'غير متوفر' : 'Not Available' })}
              </div>
            </div>
          </div>

          {/* صافي الراتب */}
          <div>
            <h3 style={{ 
              color: 'white',
              marginBottom: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign,
              direction: dir
            }}>
              {t('payslips.details.netSalary', { defaultValue: isRTL ? 'صافي الراتب' : 'Net Salary' })}
            </h3>
            <div style={{
              backgroundColor: '#1a1f2e',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #333',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: 'bold',
                color: 'white'
              }}>
                {payslipDetails.net_salary ? formatCurrency(payslipDetails.net_salary) : t('payslips.notAvailable', { defaultValue: isRTL ? 'غير متوفر' : 'Not Available' })}
              </div>
            </div>
          </div>
        </div>

        {payslipDetails.breakdown && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#1a1f2e',
            borderRadius: '8px',
            border: '1px solid #333'
          }}>
            <h3 style={{ 
              color: 'white',
              marginBottom: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              direction: dir
            }}>
              {t('payslips.details.breakdown.title', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Account Breakdown' })}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {payslipDetails.breakdown.base_salary && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.baseSalary', { defaultValue: isRTL ? 'الراتب الأساسي' : 'Base Salary' })}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                    {formatCurrency(payslipDetails.breakdown.base_salary)}
                  </div>
                </div>
              )}
              {payslipDetails.breakdown.allowances && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.totalAllowances', { defaultValue: isRTL ? 'إجمالي البدلات' : 'Total Allowances' })}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                    {formatCurrency(
                      (payslipDetails.breakdown.allowances?.housing || 0) + 
                      (payslipDetails.breakdown.allowances?.transportation || 0)
                    )}
                  </div>
                </div>
              )}
              {payslipDetails.breakdown.fixed_deductions && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.fixedDeductions', { defaultValue: isRTL ? 'الخصومات الثابتة' : 'Fixed Deductions' })}</div>
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                    {formatCurrency(payslipDetails.breakdown.fixed_deductions?.social_insurance || 0)}
                  </div>
                </div>
              )}
              {payslipDetails.breakdown.calculation_details && (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.totalWorkDays', { defaultValue: isRTL ? 'إجمالي أيام العمل' : 'Total Work Days' })}</div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                      {payslipDetails.breakdown.calculation_details.total_work_days || 0}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.attendedDays', { defaultValue: isRTL ? 'أيام الحضور' : 'Attended Days' })}</div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                      {payslipDetails.breakdown.calculation_details.attended_days || 0}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.absentDays', { defaultValue: isRTL ? 'أيام الغياب' : 'Absent Days' })}</div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                      {payslipDetails.breakdown.calculation_details.absent_days || 0}
                    </div>
                  </div>
                  {payslipDetails.breakdown.calculation_details.absence_deduction && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('payslips.details.breakdown.absenceDeduction', { defaultValue: isRTL ? 'خصم الغياب' : 'Absence Deduction' })}</div>
                      <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                        {formatCurrency(payslipDetails.breakdown.calculation_details.absence_deduction)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipDetails;


